import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, ChevronDown, Menu, X, LayoutDashboard } from "lucide-react";
import { Button } from "../ui/button";
import Logo from "../Logo";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { ROLE_HOME } from "../../lib/format";

const LINKS = [
  { key: "beranda", to: "/", route: true },
  { key: "caraKerja", to: "/#cara-kerja" },
  { key: "layanan", to: "/#layanan" },
  { key: "inspector", to: "/inspector", route: true },
  { key: "harga", to: "/#harga" },
  { key: "tentangKami", to: "/#tentang-kami" },
];

function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center h-9 rounded-lg border border-slate-200 overflow-hidden text-xs font-bold" data-testid="lang-toggle">
      {["id", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
          className={`h-full px-3 transition-colors ${lang === l ? "bg-blue-600 text-white" : "bg-white text-slate-500 hover:text-slate-800"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function PublicNavbar({ onLogin, active }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const goDash = () => navigate(user?.role ? ROLE_HOME[user.role] : "/pilih-peran");

  const renderLink = (l, mobile = false) => {
    const isActive = active === l.key;
    const cls = mobile
      ? `block py-2 text-sm font-semibold ${isActive ? "text-blue-600" : "text-slate-600"}`
      : `relative text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-900"}`;
    const onClick = mobile ? () => setOpen(false) : undefined;
    const content = (
      <>
        {t(`nav.${l.key}`)}
        {!mobile && isActive && <span className="absolute -bottom-[26px] left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
      </>
    );
    return l.route
      ? <Link key={l.key} to={l.to} onClick={onClick} data-testid={`nav-${l.key}`} className={cls}>{content}</Link>
      : <a key={l.key} href={l.to} onClick={onClick} data-testid={`nav-${l.key}`} className={cls}>{content}</a>;
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="shrink-0"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">{LINKS.map((l) => renderLink(l))}</nav>
        <div className="hidden lg:flex items-center gap-2.5 shrink-0">
          <LangToggle />
          <button className="flex items-center gap-1.5 h-9 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg px-3 hover:border-blue-300 transition-colors whitespace-nowrap">
            <MapPin size={15} className="text-slate-500" /> {user?.city || "Jakarta"} <ChevronDown size={14} className="text-slate-400" />
          </button>
          {user ? (
            <Button onClick={goDash} data-testid="nav-dashboard-btn" className="h-9 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-5"><LayoutDashboard size={15} className="mr-1.5" /> {t("nav.dashboard")}</Button>
          ) : (
            <>
              <button onClick={onLogin} data-testid="nav-login-btn" className="text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors px-2 whitespace-nowrap">{t("nav.masuk")}</button>
              <Button onClick={onLogin} data-testid="nav-register-btn" className="h-9 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-6">{t("nav.daftar")}</Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <LangToggle />
          <button className="p-2 text-slate-700" onClick={() => setOpen(!open)} aria-label="menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-1">
          {LINKS.map((l) => renderLink(l, true))}
          <div className="flex gap-3 pt-3">
            {user ? <Button onClick={goDash} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">{t("nav.dashboard")}</Button> : (
              <>
                <Button variant="outline" onClick={onLogin} className="flex-1">{t("nav.masuk")}</Button>
                <Button onClick={onLogin} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">{t("nav.daftar")}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
